import { DJEngine } from './audioEngine.js';
import { generateProceduralBeats, generateOneShotSFX } from './beats.js';

// Initialize DJ Engine & One-Shot Samples
const dj = new DJEngine();
let trackCrate = [];
let oneShotSFX = null;

// Track Metadata State
const state = {
  deckA: {
    title: 'Boom Bap Groove (90 BPM)',
    artist: 'LocalFilm Beats',
    bpm: 90,
    baseBpm: 90,
    rotation: 0
  },
  deckB: {
    title: 'Deep House Club (124 BPM)',
    artist: 'Dublin Sound Lab',
    bpm: 124,
    baseBpm: 124,
    rotation: 0
  }
};

// Setup VU Meter LEDs
function initVUMeters() {
  const meterLeft = document.getElementById('meter-left');
  meterLeft.innerHTML = '';
  for (let i = 0; i < 16; i++) {
    const led = document.createElement('div');
    led.className = 'meter-led';
    if (i >= 13) led.dataset.type = 'red';
    else if (i >= 9) led.dataset.type = 'yellow';
    else led.dataset.type = 'green';
    meterLeft.appendChild(led);
  }
}

// Populate Crate & Load Initial Beats
function initCrateAndPresets() {
  trackCrate = generateProceduralBeats(dj.ctx);
  oneShotSFX = generateOneShotSFX(dj.ctx);

  renderCrateTable();

  // Load defaults into Deck A & Deck B
  loadTrackToDeck('A', trackCrate[0]);
  loadTrackToDeck('B', trackCrate[1]);
}

function renderCrateTable() {
  const tbody = document.getElementById('crate-track-list');
  tbody.innerHTML = '';

  trackCrate.forEach((track, index) => {
    const tr = document.createElement('tr');
    tr.innerHTML = `
      <td style="color: var(--text-muted); font-family: var(--font-mono);">${index + 1}</td>
      <td style="font-weight: 700; color: #fff;">${track.name}</td>
      <td style="color: var(--text-muted);">${track.artist || 'LocalFilm'}</td>
      <td style="font-family: var(--font-mono); font-weight: 800; color: var(--accent-blue);">${track.bpm}</td>
      <td style="font-family: var(--font-mono); color: var(--text-muted);">${track.duration || '0:15'}</td>
      <td style="text-align: right;">
        <button class="btn-load-deck load-A" data-idx="${index}" data-deck="A">LOAD A</button>
        <button class="btn-load-deck load-B" data-idx="${index}" data-deck="B">LOAD B</button>
      </td>
    `;
    tbody.appendChild(tr);
  });

  // Attach load buttons
  tbody.querySelectorAll('.btn-load-deck').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const idx = parseInt(e.target.dataset.idx, 10);
      const deckId = e.target.dataset.deck;
      loadTrackToDeck(deckId, trackCrate[idx]);
    });
  });

  document.getElementById('crate-count').textContent = trackCrate.length;
  document.getElementById('crate-badge-text').textContent = `${trackCrate.length} TRACKS LOADED`;
}

function loadTrackToDeck(deckId, track) {
  dj.loadAudioBuffer(deckId, track.buffer);

  const s = deckId === 'A' ? state.deckA : state.deckB;
  s.title = track.name;
  s.artist = track.artist || 'LocalFilm';
  s.baseBpm = track.bpm;
  s.bpm = track.bpm;

  document.getElementById(`deck-${deckId}-title`).textContent = track.name;
  document.getElementById(`deck-${deckId}-artist`).textContent = s.artist;
  document.getElementById(`deck-${deckId}-bpm`).textContent = `${track.bpm.toFixed(1)} BPM`;

  // Reset Cue and Loop visual buttons
  document.querySelectorAll(`#deck-${deckId} .pad-btn`).forEach(b => b.classList.remove('active-cue'));
  document.querySelectorAll(`#deck-${deckId} .loop-btn`).forEach(b => b.classList.remove('active-loop'));
}

// Setup Event Listeners
function initControls() {
  // Crate Panel Toggle
  const cratePanel = document.getElementById('crate-panel');
  const toggleBtn = document.getElementById('btn-toggle-crate');
  const closeBtn = document.getElementById('btn-crate-close');

  toggleBtn.addEventListener('click', () => {
    cratePanel.classList.toggle('collapsed');
  });

  closeBtn.addEventListener('click', () => {
    cratePanel.classList.add('collapsed');
  });

  // Crate Multiple File Importer
  document.getElementById('crate-file-input').addEventListener('change', async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      try {
        const arrayBuf = await file.arrayBuffer();
        const audioBuf = await dj.ctx.decodeAudioData(arrayBuf);
        const mins = Math.floor(audioBuf.duration / 60);
        const secs = Math.floor(audioBuf.duration % 60);

        trackCrate.push({
          id: `file_${Date.now()}_${Math.random()}`,
          name: file.name.replace(/\.[^/.]+$/, ""),
          artist: 'Local Import',
          bpm: 120, // Default estimate
          duration: `${mins}:${secs < 10 ? '0' : ''}${secs}`,
          buffer: audioBuf
        });
      } catch (err) {
        console.error("Audio decode error:", err);
      }
    }
    renderCrateTable();
  });

  // Sampler One-Shot SFX Buttons
  document.getElementById('sfx-airhorn').addEventListener('click', () => dj.playOneShotSFX(oneShotSFX.airhorn));
  document.getElementById('sfx-rewind').addEventListener('click', () => dj.playOneShotSFX(oneShotSFX.rewind));
  document.getElementById('sfx-siren').addEventListener('click', () => dj.playOneShotSFX(oneShotSFX.dubSiren));
  document.getElementById('sfx-laser').addEventListener('click', () => dj.playOneShotSFX(oneShotSFX.laser));

  // Play / Pause Buttons
  ['A', 'B'].forEach((deckId) => {
    const playBtn = document.getElementById(`deck-${deckId}-play`);
    playBtn.addEventListener('click', () => {
      const deck = deckId === 'A' ? dj.deckA : dj.deckB;
      if (deck.isPlaying) {
        dj.pauseDeck(deckId);
        playBtn.classList.remove('playing');
        playBtn.textContent = 'PLAY';
      } else {
        dj.playDeck(deckId);
        playBtn.classList.add('playing');
        playBtn.textContent = 'PAUSE';
      }
    });

    // Cue Buttons
    const cueBtn = document.getElementById(`deck-${deckId}-cue`);
    cueBtn.addEventListener('click', () => {
      dj.jumpToCue(deckId);
      const deck = deckId === 'A' ? dj.deckA : dj.deckB;
      if (!deck.isPlaying) {
        playBtn.classList.remove('playing');
        playBtn.textContent = 'PLAY';
      }
    });

    // Motor Brake Button
    document.getElementById(`deck-${deckId}-brake`).addEventListener('click', () => {
      dj.triggerBrake(deckId);
      playBtn.classList.remove('playing');
      playBtn.textContent = 'PLAY';
    });

    // Sync Button
    document.getElementById(`deck-${deckId}-sync`).addEventListener('click', () => {
      const targetBpm = deckId === 'A' ? state.deckB.bpm : state.deckA.bpm;
      const s = deckId === 'A' ? state.deckA : state.deckB;
      const pitchOffset = ((targetBpm - s.baseBpm) / s.baseBpm) * 100;
      const clampedPitch = Math.max(-50, Math.min(50, pitchOffset));
      document.getElementById(`deck-${deckId}-pitch`).value = clampedPitch;
      dj.setPitch(deckId, clampedPitch);
      updateBpmDisplay(deckId);
    });

    // Pitch Slider
    const pitchSlider = document.getElementById(`deck-${deckId}-pitch`);
    pitchSlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      dj.setPitch(deckId, val);
      updateBpmDisplay(deckId);
    });

    document.getElementById(`deck-${deckId}-pitch-reset`).addEventListener('click', () => {
      pitchSlider.value = 0;
      dj.setPitch(deckId, 0);
      updateBpmDisplay(deckId);
    });

    // Hot Cues (1-4)
    for (let c = 0; c < 4; c++) {
      const cuePad = document.getElementById(`deck-${deckId}-cue-${c + 1}`);
      cuePad.addEventListener('click', () => {
        const res = dj.triggerHotCue(deckId, c);
        if (res) {
          cuePad.classList.add('active-cue');
          playBtn.classList.add('playing');
          playBtn.textContent = 'PAUSE';
        }
      });

      // Shift+Click or Double click clears hot cue
      cuePad.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        dj.clearHotCue(deckId, c);
        cuePad.classList.remove('active-cue');
      });
    }

    // Auto Loop Buttons
    [1, 2, 4, 8].forEach((beats) => {
      const loopBtn = document.getElementById(`deck-${deckId}-loop-${beats}`);
      loopBtn.addEventListener('click', () => {
        const s = deckId === 'A' ? state.deckA : state.deckB;
        const isActive = dj.toggleLoop(deckId, beats, s.bpm);
        document.querySelectorAll(`#deck-${deckId} .loop-btn`).forEach(b => b.classList.remove('active-loop'));
        if (isActive) {
          loopBtn.classList.add('active-loop');
        }
      });
    });

    // Channel Volume
    document.getElementById(`fader-${deckId}-vol`).addEventListener('input', (e) => {
      dj.setVolume(deckId, parseFloat(e.target.value));
    });

    // Scratchable Vinyl Platter
    setupVinylScratch(deckId, `vinyl-${deckId}`);
  });

  // Crossfader
  document.getElementById('crossfader').addEventListener('input', (e) => {
    dj.updateCrossfader(parseFloat(e.target.value));
  });

  // Rotary EQ & Filter Knobs
  setupRotaryKnobs();

  // Master Mix Recording
  const recordBtn = document.getElementById('btn-record-mix');
  const recordText = document.getElementById('record-btn-text');
  recordBtn.addEventListener('click', async () => {
    if (!dj.isRecording) {
      dj.startRecording();
      recordBtn.classList.add('recording');
      recordText.textContent = 'Stop & Save';
    } else {
      const blob = await dj.stopRecording();
      recordBtn.classList.remove('recording');
      recordText.textContent = 'Record Mix';

      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `LocalTurntables_Mix_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.webm`;
        a.click();
      }
    }
  });
}

function updateBpmDisplay(deckId) {
  const s = deckId === 'A' ? state.deckA : state.deckB;
  const deck = deckId === 'A' ? dj.deckA : dj.deckB;
  const pitchPercent = deck.pitchPercent;
  s.bpm = s.baseBpm * (1 + pitchPercent / 100);

  document.getElementById(`deck-${deckId}-bpm`).textContent = `${s.bpm.toFixed(1)} BPM`;
  document.getElementById(`deck-${deckId}-pitch-val`).textContent = `${pitchPercent >= 0 ? '+' : ''}${pitchPercent.toFixed(1)}%`;
}

// Rotary EQ and Filter Knobs Handling
function setupRotaryKnobs() {
  const knobs = document.querySelectorAll('.rotary-knob');
  knobs.forEach((knob) => {
    let startY = 0;
    let currentValue = 0; // EQ: -48 to +12dB (center 0). Filter: -100 to +100 (center 0)
    const isFilter = knob.classList.contains('knob-filter');
    const deckId = knob.dataset.deck;
    const band = knob.dataset.band;

    const onPointerMove = (e) => {
      const deltaY = startY - e.clientY;
      startY = e.clientY;

      if (isFilter) {
        currentValue = Math.max(-100, Math.min(100, currentValue + deltaY * 1.5));
        const angle = (currentValue / 100) * 135;
        knob.style.transform = `rotate(${angle}deg)`;
        dj.setSweepFilter(deckId, currentValue);
      } else {
        currentValue = Math.max(-48, Math.min(12, currentValue + deltaY * 0.5));
        const angle = (currentValue / (currentValue >= 0 ? 12 : 48)) * 135;
        knob.style.transform = `rotate(${angle}deg)`;
        dj.setEQ(deckId, band, currentValue);
      }
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    knob.addEventListener('pointerdown', (e) => {
      startY = e.clientY;
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    });

    knob.addEventListener('dblclick', () => {
      currentValue = 0;
      knob.style.transform = 'rotate(0deg)';
      if (isFilter) dj.setSweepFilter(deckId, 0);
      else dj.setEQ(deckId, band, 0);
    });
  });
}

// Vinyl Scratch Touch & Physics
function setupVinylScratch(deckId, vinylElemId) {
  const vinyl = document.getElementById(vinylElemId);
  let isDragging = false;

  const getAngle = (clientX, clientY) => {
    const rect = vinyl.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const rad = Math.atan2(clientY - centerY, clientX - centerX);
    return rad * (180 / Math.PI);
  };

  vinyl.addEventListener('pointerdown', (e) => {
    isDragging = true;
    vinyl.setPointerCapture(e.pointerId);
    const angle = getAngle(e.clientX, e.clientY);
    dj.startScratch(deckId, angle);
  });

  vinyl.addEventListener('pointermove', (e) => {
    if (!isDragging) return;
    const angle = getAngle(e.clientX, e.clientY);
    dj.updateScratch(deckId, angle);
    
    const s = deckId === 'A' ? state.deckA : state.deckB;
    s.rotation = angle;
    vinyl.style.transform = `rotate(${angle}deg)`;
  });

  const stopScratch = (e) => {
    if (isDragging) {
      isDragging = false;
      dj.endScratch(deckId);
      try { vinyl.releasePointerCapture(e.pointerId); } catch (err) {}
    }
  };

  vinyl.addEventListener('pointerup', stopScratch);
  vinyl.addEventListener('pointercancel', stopScratch);
}

// Frequency-Colored Waveform Visualizer
function drawWaveforms() {
  const drawDeckWaveform = (deckId, canvasId) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.clientWidth;
    const height = canvas.height = canvas.clientHeight;

    ctx.fillStyle = '#05070a';
    ctx.fillRect(0, 0, width, height);

    const pos = dj.getCurrentPlayPosition(deckId);
    const deck = deckId === 'A' ? dj.deckA : dj.deckB;

    if (deck.buffer) {
      const data = deck.buffer.getChannelData(0);
      const step = Math.ceil(data.length / width);
      const amp = height / 2;

      // Frequency split color gradient (Bass = Cyan/Blue, Mid = Purple/Pink)
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      if (deckId === 'A') {
        grad.addColorStop(0, '#38bdf8');
        grad.addColorStop(0.5, '#06b6d4');
        grad.addColorStop(1, '#0284c7');
      } else {
        grad.addColorStop(0, '#ec4899');
        grad.addColorStop(0.5, '#a855f7');
        grad.addColorStop(1, '#7c3aed');
      }

      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.8;
      ctx.beginPath();

      for (let i = 0; i < width; i++) {
        let min = 1.0;
        let max = -1.0;
        for (let j = 0; j < step; j++) {
          const datum = data[(i * step) + j];
          if (datum < min) min = datum;
          if (datum > max) max = datum;
        }
        ctx.moveTo(i, (1 + min) * amp);
        ctx.lineTo(i, (1 + max) * amp);
      }
      ctx.stroke();

      // Draw Hot Cue Markers on Waveform
      deck.hotCues.forEach((cueTime, cueIdx) => {
        if (cueTime !== null && deck.duration > 0) {
          const cueX = (cueTime / deck.duration) * width;
          ctx.fillStyle = deckId === 'A' ? '#38bdf8' : '#a855f7';
          ctx.fillRect(cueX - 1, 0, 3, height);
          ctx.font = '9px monospace';
          ctx.fillText(`C${cueIdx + 1}`, cueX + 3, 10);
        }
      });

      // Playhead Marker
      const playheadX = pos.progress * width;
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = '#ffffff';
      ctx.shadowBlur = 8;
      ctx.fillRect(playheadX - 1, 0, 2, height);
      ctx.shadowBlur = 0;
    }
  };

  drawDeckWaveform('A', 'deck-A-waveform');
  drawDeckWaveform('B', 'deck-B-waveform');
}

// Master Audio Loop (Platter Spin & VU Meters)
function startAnimationLoop() {
  const leds = document.querySelectorAll('#meter-left .meter-led');
  const buffer = new Uint8Array(dj.masterAnalyser.frequencyBinCount);

  let lastTime = performance.now();

  function tick(now) {
    const dt = (now - lastTime) / 1000;
    lastTime = now;

    // Rotate Platters if playing and not scratching
    ['A', 'B'].forEach((deckId) => {
      const deck = deckId === 'A' ? dj.deckA : dj.deckB;
      const s = deckId === 'A' ? state.deckA : state.deckB;
      const vinyl = document.getElementById(`vinyl-${deckId}`);

      if (deck.isPlaying && !deck.isScratching) {
        const dAngle = 200 * deck.playbackRate * dt;
        s.rotation = (s.rotation + dAngle) % 360;
        vinyl.style.transform = `rotate(${s.rotation}deg)`;
      }
    });

    drawWaveforms();

    // Master VU Meter
    dj.masterAnalyser.getByteFrequencyData(buffer);
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      sum += buffer[i];
    }
    const avg = sum / buffer.length;
    const activeLedCount = Math.round((avg / 128) * leds.length);

    leds.forEach((led, idx) => {
      if (idx < activeLedCount) {
        if (led.dataset.type === 'red') led.className = 'meter-led active-red';
        else if (led.dataset.type === 'yellow') led.className = 'meter-led active-yellow';
        else led.className = 'meter-led active-green';
      } else {
        led.className = 'meter-led';
      }
    });

    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
}

// Splash Screen
function initSplashScreen() {
  const splash = document.getElementById('splash-screen');
  const progressBar = document.querySelector('.splash-loader-progress');
  const statusText = document.getElementById('splash-status-text');
  const startBtn = document.getElementById('splash-start-btn');

  const steps = [
    { pct: 25, text: 'CALIBRATING 64-BIT AUDIO CONTEXT...' },
    { pct: 55, text: 'SYNTHESIZING SOUNDPACK & PERFORMANCE PADS...' },
    { pct: 85, text: 'MOUNTING DUAL PLATTERS & MIXER BUS...' },
    { pct: 100, text: 'STUDIO DJ CONSOLE READY' }
  ];

  let currentStep = 0;
  const interval = setInterval(() => {
    if (currentStep < steps.length) {
      const s = steps[currentStep];
      progressBar.style.width = `${s.pct}%`;
      statusText.textContent = s.text;
      currentStep++;
    } else {
      clearInterval(interval);
      startBtn.classList.add('ready');
    }
  }, 350);

  const dismissSplash = () => {
    dj.ensureContext();
    splash.classList.add('hidden');
    setTimeout(() => {
      splash.remove();
    }, 700);
  };

  startBtn.addEventListener('click', dismissSplash);
  splash.addEventListener('click', (e) => {
    if (e.target === splash || e.target.classList.contains('splash-hero-logo')) {
      dismissSplash();
    }
  });
}

// Boot System
window.addEventListener('DOMContentLoaded', () => {
  initSplashScreen();
  initVUMeters();
  initCrateAndPresets();
  initControls();
  startAnimationLoop();
});
